import { ChannelSubSection, OrderType } from '@modules/common'
import {
  CATEGORY_UI_SEPERATOR,
  CLIENT_ORDER_LEVEL_GROUP_ATTR,
  DOOR_KPI_ORDER_LEVEL_AGGR,
  DOOR_ORDER_LEVEL_GROUP_ATTR,
  KPI_TOPBAR_METRIC_ATTRIBUTES,
  MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_RETAIL_QUANTITY,
  MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_RETAIL_SUMMARY,
  MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_WHOLESALE,
  METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
  METRIC_ATTRIBUTES_FOR_IA_RETAIL_QUANTITY,
  METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
  METRIC_ATTRIBUTES_FOR_RETAIL_SUMMARY,
  PRODUCT_ATTRIBUTES_FOR_ROLLUP,
  PRODUCT_REMOVE_LOADER_KEY,
  WANING_DROPDOWN_DEFAULT_FILTER_NAME
} from '@modules/order/constants/uiConstants'
import { CancellableRequest } from '@services/HttpService'
import CommentService from '@services/commentService'
import { stores } from '@stores'
import { nav } from '@stores/nav'
import { PaginatedHierarchyItemsDataStore } from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsDataStore'
import find from 'lodash/find'
import intersectionBy from 'lodash/intersectionBy'
import join from 'lodash/join'
import map from 'lodash/map'
import omit from 'lodash/omit'
import reduce from 'lodash/reduce'
import { action, computed, observable, reaction, toJS } from 'mobx'
import { orderKPIGlobalTopBarStore } from '@modules/order/stores/OrderKPIGlobalTopBarStore'
import { getChannelName, getTitleCaseChannelName } from '@utils/get-channel-name'
import { config } from '@config'
import { DefaultConfig } from '@constants'
import {
  MANDATORY_ORDER_ATTRIBUTE_FIELDS,
  PRODUCT_SERVICE_ENDPOINTS
} from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'
import EnhancedListDataService from '@modules/order/services/EnhancedListDataService'
import {
  addHierarchyNamesToProductsData,
  formatProductGroupsData,
  getCurrentGroupMapping,
  mapNewFiltersToIds,
  parseAppliedSort
} from '@modules/order/utils/dynamic-group-mapping'
import { isEmptyValue } from '@utils/object-utils'
import { cloneDeep, differenceBy, filter, uniq } from 'lodash'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { ListTableView } from './EnhancedListViewTableService'
import { EnhancedWarningPopupStore } from '../../components/EnhancedWarningButton/EnhancedWarningPopupStore'
import { CURVE_TYPES } from '@modules/showroom/EnhancedOrder/constants/uiConstants'
import { DebouncePool } from '@utils/debounce-queue/DebouncePool'
import { mergeDeep } from '@apollo/client/utilities'

export enum PRODUCT_OPERATIONS_LOADERS {
  UPDATING_DRAFT_QUANTITY = 'UPDATING_DRAFT_QUANTITY',
  BOOK_DOOR = 'BOOK_DOOR',
  UNBOOK_DOOR = 'UNBOOK_DOOR',
  UPDATING_DOOR_DRAFT_QUANTITY = 'UPDATING_DOOR_DRAFT_QUANTITY',
  BOOKING_ALL_DOORS = 'BOOKING_ALL_DOORS',
  UNBOOKING_ALL_DOORS = 'UNBOOKING_ALL_DOORS',
  BOOK_UNBOOK_IN_PROGRESS = 'BOOK_UNBOOK_IN_PROGRESS',
  UPDATING_CLIENT_ORDER_DRAFT_QUANTITY = 'UPDATING_CLIENT_ORDER_DRAFT_QUANTITY',
  SPLITTING_CLIENT_ORDER_QUANTITY = 'SPLITTING_CLIENT_ORDER_QUANTITY',
  SPLITTING_CLIENT_ORDER_SIZE_QUANTITY = 'SPLITTING_CLIENT_ORDER_SIZE_QUANTITY',
  UPDATING_COMMENT = 'UPDATING_COMMENT'
}

export interface ProductsWithoutCurves {
  curveTypeToBeUsed: string
  productsWithoutDoorCurve: string[]
  productsWithoutSizeCurve: string[]
}

export class EnhancedListDataStore extends PaginatedHierarchyItemsDataStore {
  private overviewReactions = []
  @observable.shallow hierarchyWithoutFilters = []
  @observable currentViewName = 'orderCollection'
  @observable totalDoorKPIs = []
  @observable productsWithoutCurves: ProductsWithoutCurves = {
    productsWithoutDoorCurve: [],
    productsWithoutSizeCurve: [],
    curveTypeToBeUsed: CURVE_TYPES.DOOR_CURVE
  }

  private service = new EnhancedListDataService()
  private commentService = new CommentService()
  enhancedWarningPopupStore = new EnhancedWarningPopupStore()
  private debouncePool = new DebouncePool({ wait: 1500, useLeading: true, maxWait: 5000 })

  getIAProductDataLoading: any = id => false

  getIAProductData: any = id => null

  @computed get displayAvgDepthAndTotalStoresInQtyView() {
    return (
      (config.displayAvgDepthAndTotalStoresInQtyView ||
        DefaultConfig.DISPLAY_AVG_DEPTH_AND_TOTAL_STORES_IN_QTY_VIEW) === 'true'
    )
  }

  @computed get avgDepthAndTotalStoresAttributes() {
    return this.displayAvgDepthAndTotalStoresInQtyView
      ? ['numberOfStores', 'averageDepth']
      : []
  }

  fetchItemsFromAPI(
    fromIndex: number,
    toIndex: number,
    callBack?: Function
  ): CancellableRequest<any> | Promise<any[]> {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientType,
          subSection
        }
      },
      productConfiguration: {
        findSpecificAttribute,
        defaultValuesFromConfig,
        getViewProductCardAttributes,
        initialAllocationActivated
      }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    let mapping = getCurrentGroupMapping(
      this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0),
      defaultValuesFromConfig?.empty,
      findSpecificAttribute
    )

    const channelName = getChannelName(clientType)
    const attrConfig = getViewProductCardAttributes(channelName, this.currentViewName)

    const body = {
      includeAllDoorOrders: true,
      ...this.getAllTopBarSelections(true),
      filters: {
        ...(mapping ? mapping : {}),
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...this.getAppliedWarningFilterData()
      },
      // ToDo: uncomment below for GBB-11416, once backend issue resolved
      // excludeFilters: orderType === OrderType.Client ? { doorOrderProductDeleted: [true] } : {},
      attributes: [
        ...uniq(
          MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(
            attrConfig.map(item => item.attributeId)
          )
        ),
        ...this.getListOfWarningAttributes(),
        ...(clientType === getEnumValue(VIPClientType.Internal)
          ? subSection === ListTableView.Quantity
            ? MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_RETAIL_QUANTITY
            : MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_RETAIL_SUMMARY
          : MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_WHOLESALE),
        ...(initialAllocationActivated ? ['forecastQuantity'] : [])
      ]
    }
    return this.service
      .fetchProductsByGrouping(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
      .then(resp => {
        callBack?.(fromIndex, toIndex)
        return addHierarchyNamesToProductsData(resp.data, mapping)
      })
  }

  @action
  getListOfWarningAttributes = () => {
    const {
      productConfiguration: { getOrderValidationWarningsAttributes, isWarningModeActive }
    } = stores
    return isWarningModeActive
      ? map(getOrderValidationWarningsAttributes(), 'filterName')
      : []
  }

  fetchHierarchyFromAPI(forRefresh?: boolean): CancellableRequest<any> | Promise<any> {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          subSection,
          clientType
        }
      },
      productConfiguration: { defaultValuesFromConfig, initialAllocationActivated }
    } = stores

    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    const addedGroups = this.cnfGroupByUIStore.addedGroupByAttributes

    const isSummaryListView =
      clientType === getEnumValue(VIPClientType.Internal) &&
      (subSection === ListTableView.Summary || !subSection)
    const groupByDoors = addedGroups?.length
      ? {
          groupBy: [
            ...[
              {
                attributeId: 'doorId',
                orderBy: 'asc'
              }
            ],
            ...addedGroups
              .filter(({ id }) => id)
              .map(({ id, sortId }) => ({ attributeId: id, orderBy: sortId }))
          ]
        }
      : {
          preAdditionalDefaultGroupBy: [
            {
              attributeId: 'doorId',
              orderBy: 'asc'
            }
          ]
        }

    const body = {
      heirarchy: {
        ...this.getAllTopBarSelections(false),
        metricAttributes: isSummaryListView
          ? METRIC_ATTRIBUTES_FOR_RETAIL_SUMMARY
          : [
              'totalProductQuantity',
              'countOfClientOrderProductId',
              ...this.avgDepthAndTotalStoresAttributes,
              ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
              ...(initialAllocationActivated
                ? METRIC_ATTRIBUTES_FOR_IA_RETAIL_QUANTITY
                : [])
            ]
      },
      ...(!isSummaryListView
        ? {
            totalDoorHierarchyKPIs: {
              metricAttributes: [
                ...['totalProductQuantity'],
                ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
                ...(initialAllocationActivated
                  ? METRIC_ATTRIBUTES_FOR_IA_RETAIL_QUANTITY
                  : [])
              ],
              ...this.getAllTopBarSelections(true),
              ...groupByDoors
            }
          }
        : {}),
      orderTopbarKPIs: {
        metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
        groupBy: [
          orderType === OrderType.Door
            ? DOOR_ORDER_LEVEL_GROUP_ATTR
            : CLIENT_ORDER_LEVEL_GROUP_ATTR
        ]
      }
    }

    return this.service
      .fetchOrderProductGroups(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
      .then(resp => {
        const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = resp?.data
        this.processNewHierarchy(totalDoorHierarchyKPIs)
        this.totalDoorKPIs = totalDoorHierarchyKPIs?.children ?? []
        const tempData = cloneDeep(heirarchy)
        this.processNewHierarchy(tempData)
        formatProductGroupsData(tempData, defaultValuesFromConfig?.empty)
        const topbarData = {
          ...orderTopbarKPIs,
          totalProducts:
            orderTopbarKPIs.totalProducts ?? orderTopbarKPIs.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        if (forRefresh) {
          this.heirarchy = tempData?.children ?? []
        }
        return tempData?.children ?? []
      })
      .catch(e => {
        console.error(e)
        throw new Error('bb.settings.productCardConfigSettings.fetchHierarchy')
      })
  }

  fetchFilterAttributesFromAPI(): CancellableRequest<any> | Promise<any> {
    return undefined
  }

  @action
  prepareDataForRemove = (productId, orderType) => {
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)

    const body = {
      aggregations: {
        heirarchy: {
          ...this.getPayloadForUpdatedPageLevelHeirarchy(product),
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
          ...this.getPayloadForUpdatedDoorLevelHeirarchy()
        },
        orderTopbarKPIs: {
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
          groupBy: [
            orderType === OrderType.Door
              ? DOOR_ORDER_LEVEL_GROUP_ATTR
              : CLIENT_ORDER_LEVEL_GROUP_ATTR
          ]
        }
      }
    }
    return body
  }

  afterRemoveItemAPI = (respData, defaultValuesFromConfig) => {
    const { totalDoorHierarchyKPIs, heirarchy, orderTopbarKPIs } = respData
    //Updating heirarchy
    this.processNewHierarchy(totalDoorHierarchyKPIs)
    this.processNewHierarchy(heirarchy)
    formatProductGroupsData(totalDoorHierarchyKPIs, defaultValuesFromConfig?.empty)
    //Updating Topbar
    const topbar = {
      totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
      totalProductValue: orderTopbarKPIs?.totalProductValue,
      totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
      totalDoorValue: orderTopbarKPIs?.totalDoorValue,
      totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
      totalSizeValue: orderTopbarKPIs?.totalSizeValue,
      totalProducts:
        orderTopbarKPIs?.totalProducts ?? orderTopbarKPIs?.countOfClientOrderProductId
    }
    orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbar)
  }

  removeItemAPI(productId: string) {
    const {
      nav: {
        queryParams: { orderId, orderType, buyingSessionGroupId, clientOrderId }
      },
      productConfiguration: { defaultValuesFromConfig }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    const body = this.prepareDataForRemove(productId, orderType)

    const res = this.service
      .removeProductFromEnhancedOrder(
        productId,
        orderId,
        buyingSessionGroupId,
        clientOrderId,
        body,
        orderType
      )
      .then(respData => {
        this.afterRemoveItemAPI(respData, defaultValuesFromConfig)
        return respData
      })
    return Promise.resolve(res)
  }

  removeProductFromOrderDetailsPopup = (productId: string) => {
    const {
      nav: {
        queryParams: {
          orderId,
          orderType,
          buyingSessionGroupId,
          clientOrderId,
          clientType
        }
      },
      productConfiguration: { defaultValuesFromConfig }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    const body = this.prepareDataForRemove(productId, orderType)
    const updatedDoorOrderProducts =
      clientType === getEnumValue(VIPClientType.Internal)
        ? {}
        : orderType === OrderType.Door
        ? { doorOrderSizeQuantities: [] }
        : {
            doorOrderProducts: this.enhancedCreateDoorProducts(
              Object.keys(product?.doorOrderProducts)
            )
          }

    const updatedProduct = {
      ...product,
      ...updatedDoorOrderProducts,
      clientOrderProductQuantity: 0,
      clientOrderProductValue: 0,
      doorOrderProductDoorQuantity: 0,
      doorOrderProductDoorValue: 0,
      doorOrderProductSizeQuantity: 0,
      doorOrderProductSizeValue: 0
    }

    return this.service
      .removeProductFromEnhancedOrder(
        productId,
        orderId,
        buyingSessionGroupId,
        clientOrderId,
        body,
        orderType
      )
      .then(respData => {
        this.itemIndexObjectStore.idObjectMap.set(product.id, updatedProduct)
        this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
        this.afterRemoveItemAPI(respData, defaultValuesFromConfig)
        return respData
      })
  }

  @action
  addProuductToOrderAgain = (productId, summary) => {
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const {
      nav: {
        queryParams: { orderType, buyingSessionGroupId, clientType, orderId }
      }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    const clientOrderId = summary?.clientOrder?.clientOrderId ?? summary?.clientOrder?.id
    const doorOrderIds =
      clientType === getEnumValue(VIPClientType.Internal)
        ? []
        : map(
            filter(summary.doorOrders, doorOrder => !doorOrder?.deleted),
            'id'
          )

    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      aggregations: {
        heirarchy: {
          ...this.getPayloadForUpdatedPageLevelHeirarchy(product),
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
          ...this.getPayloadForUpdatedDoorLevelHeirarchy()
        },
        orderTopbarKPIs: {
          metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
          groupBy: [
            orderType === OrderType.Door
              ? DOOR_ORDER_LEVEL_GROUP_ATTR
              : CLIENT_ORDER_LEVEL_GROUP_ATTR
          ]
        }
      }
    }
    return this.service
      .addProductToEnhancedOrder(
        productId,
        orderType !== OrderType.Door ? clientOrderId : doorOrderIds[0] ?? orderId,
        buyingSessionGroupId,
        orderType !== OrderType.Door ? '' : clientOrderId,
        body,
        orderType
      )
      .then(response => {
        const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = response
        const topbar = {
          totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
          totalProductValue: orderTopbarKPIs?.totalProductValue,
          totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
          totalDoorValue: orderTopbarKPIs?.totalDoorValue,
          totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
          totalSizeValue: orderTopbarKPIs?.totalSizeValue,
          totalProducts:
            orderTopbarKPIs?.totalProducts ?? orderTopbarKPIs?.countOfClientOrderProductId
        }

        this.mutateProductHierarchy(heirarchy, product?.hierarchyItems?.[0])
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbar)
        return response
      })
  }

  @action
  async init() {
    await super.init()
    this.overviewReactions.push(
      reaction(
        () => this.heirarchy,
        () => {
          if (!this.filterStore.appliedFilters?.length && !this.searchKey) {
            this.hierarchyWithoutFilters = toJS(this.heirarchy, {
              recurseEverything: true
            })
          }
        }
      )
    )
  }

  @action getAppliedWarningFilterData = () => {
    const {
      productConfiguration: { isWarningModeActive }
    } = stores
    let warningFilter = {}
    if (isWarningModeActive) {
      const tempVal = this.enhancedWarningPopupStore.appliedWarningFilterOption
      if (
        tempVal?.filterName &&
        tempVal.filterName !== WANING_DROPDOWN_DEFAULT_FILTER_NAME
      ) {
        warningFilter = {
          [tempVal.filterName]: tempVal.filterValues
        }
      }
    }
    return warningFilter
  }

  @action getAllTopBarSelections = (isCustomGroupByApplied: boolean) => {
    const {
      nav: {
        queryParams: { clientType }
      },
      productConfiguration: { getActionAttributes }
    } = stores

    const addedGroups = this.cnfGroupByUIStore.addedGroupByAttributes
    const channelName = getChannelName(clientType)
    const searchableAttributes = getActionAttributes(
      'searchBy',
      channelName,
      this.currentViewName,
      true
    )
    return {
      sort: parseAppliedSort(this.enhancedSortStore.selectedSortOption),
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...this.getAppliedWarningFilterData()
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...(isEmptyValue(this.searchKey)
        ? {}
        : {
            search: this.searchKey,
            searchAttributes: this.searchKey ? searchableAttributes : []
          }),
      ...(!isCustomGroupByApplied && {
        groupBy: addedGroups
          .filter(({ id }) => id)
          .map(({ id, sortId }) => ({ attributeId: id, orderBy: sortId }))
      })
    }
  }

  drainDebounceQueue = (finalAction?: () => Promise<any>) => {
    if (finalAction) {
      this.debouncePool.add(
        { key: 'drainDebounceQueue', resetTimerIf: () => true },
        finalAction
      )
    }
    this.debouncePool.drainQueue()
  }

  refreshProductRow = async (
    productId: string,
    debounced: boolean,
    attributes: string[] = PRODUCT_ATTRIBUTES_FOR_ROLLUP, // default is 👍 fine perf-wise, and better to be consistent
    resetTimerIf: (previousKey: string) => boolean = undefined
  ): Promise<boolean> => {
    if (!this.itemIndexObjectStore.idObjectMap.get(productId)) {
      return false
    }
    const fetchData = async () => this.fetchSingleProductData(productId, attributes)
    const result = debounced
      ? await this.debouncePool.add(
          {
            key: `fetchSingleProductData.productId=${productId}`,
            resetTimerIf
          },
          fetchData
        )
      : await fetchData()
    const response = debounced ? result.response : result
    if (!debounced || result.isPrimaryResponse) {
      // TODO: this merge logic should be fully vetted. For example, how can we selectively only update one affected door?
      const currentProductState = this.itemIndexObjectStore.idObjectMap.get(productId)
      const mergedObject = { ...currentProductState, ...response }
      this.updateSingleProductDataFromResponse(productId, mergedObject, response)
      return true
    }
    return false // only if debounced and isPrimaryResponse is false
  }

  /** Similar to fetchHierarchyFromAPI(), but also includes total door and size quantity numbers for a single product */
  refreshAggregateDataWithProductRowTotals = async (
    productId: string,
    bsgId: string,
    orderId: string,
    orderType: OrderType | string,
    clientOrderId: string
  ) => {
    try {
      const product = this.itemIndexObjectStore.idObjectMap.get(productId)
      const aggregateResp: { data: any } = await this.service.fetchOrderProductGroups(
        bsgId,
        orderId,
        orderType,
        clientOrderId,
        this.getPayloadForPageAndSingleProductAggregation(product, orderType)
      )
      if (!aggregateResp?.data?.heirarchy) {
        throw new Error(`No hierarchy data returned for product ${productId}`)
      }

      const {
        heirarchy,
        totalDoorHierarchyKPIs,
        orderTopbarKPIs,
        singleProductAggregation
      } = aggregateResp.data

      // Sets product-level door and sized quantity totals in the column to the left of doors
      this.updateSingleProductDoorOrderTotals(productId, singleProductAggregation)

      //Removing Product from warning mode filters if product has no warnings
      this.removeUnFilteredProduct(singleProductAggregation, productId)

      // Update Hierarchy state (fresh product lookup, since time elapsed in the fetch)
      const productHierarchy =
        this.itemIndexObjectStore.idObjectMap.get(productId)?.hierarchyItems?.[0]
      if (productHierarchy) {
        this.mutateProductHierarchy(heirarchy, productHierarchy)
      }
      this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)

      // Update Topbar state
      const topbarData = {
        totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
        totalSizeValue: orderTopbarKPIs?.totalSizeValue,
        totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
        totalDoorValue: orderTopbarKPIs?.totalDoorValue
      }
      orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
    } catch (e) {
      // TODO: is a toast shown automatically via service failure? Could also show an error on related KPIs that failed to update
      console.log('error', e)
    }
  }

  // TODO: move to service
  fetchSingleProductData = async (productId, operationAttributes = []) => {
    const {
      nav: {
        queryParams: {
          orderId,
          buyingSessionGroupId,
          orderType,
          clientOrderId,
          clientType
        }
      },
      productConfiguration: { getViewProductCardAttributes }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    const channelName = getChannelName(clientType)
    const attrConfig = getViewProductCardAttributes(channelName, this.currentViewName)
    const productPayload = {
      includeAllDoorOrders: true,
      filters: {
        clientOrderProductId: [productId]
      },
      excludeFilters: {},
      attributes: [
        ...uniq(
          MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(
            attrConfig.map(item => item.attributeId)
          )
        ),
        ...this.getListOfWarningAttributes(),
        ...operationAttributes
      ]
    }
    try {
      const resp = await this.service.fetchProductsByGrouping(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        productPayload
      )
      return resp?.data?.[0]
    } catch (e) {
      throw e
    }
  }

  @action
  updateProductQuantity(productId, payload, isSummaryListView = false) {
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    try {
      const {
        nav: {
          queryParams: { orderId, buyingSessionGroupId }
        }
      } = stores
      if (!orderId || !buyingSessionGroupId) {
        return undefined
      }
      this.itemIndexObjectStore.setRangeLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
        true
      )
      return this.service
        .updateProductQuantity(buyingSessionGroupId, productId, orderId, payload)
        .then(async response => {
          const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = response
          let oldProudctData = {
            ...product
          }
          if (payload?.rollUp) {
            const productResp: any = await this.fetchSingleProductData(productId, [
              ...PRODUCT_ATTRIBUTES_FOR_ROLLUP,
              ...(this.displayAvgDepthAndTotalStoresInQtyView ? ['averageDepth'] : [])
            ])
            oldProudctData.doorOrderProducts = productResp?.doorOrderProducts || {}
            this.updateSingleProductDataFromResponse(
              product.id,
              oldProudctData,
              productResp
            )
            this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
            // Updating Topbar
            const topbarData = {
              totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
              totalProductValue: orderTopbarKPIs?.totalProductValue,
              totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
              totalDoorValue: orderTopbarKPIs?.totalDoorValue,
              totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
              totalSizeValue: orderTopbarKPIs?.totalSizeValue
            }
            orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
          } else {
            const productResp = await this.fetchSingleProductData(productId, [
              'clientOrderProductQuantity',
              'clientOrderProductValue',
              'clientOrderProductGradingAttributes',
              ...(this.displayAvgDepthAndTotalStoresInQtyView || isSummaryListView
                ? ['averageDepth']
                : []),
              ...(isSummaryListView ? ['averageWeeklySales', 'salesQuantity'] : [])
            ])
            this.updateSingleProductDataFromResponse(
              product.id,
              oldProudctData,
              productResp
            )

            // Updating Topbar
            const topbarData = {
              totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
              totalProductValue: orderTopbarKPIs?.totalProductValue
            }
            orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
          }
          //Updating Heirarchy
          this.mutateProductHierarchy(heirarchy, product?.hierarchyItems?.[0])
          //Removing Product from warning mode filters if product has no warnings
          this.removeUnFilteredProduct(response?.singleProductAggregation, productId)
          this.setQuantityUpdated(true)
          this.itemIndexObjectStore.setRangeLoading(
            productIndex,
            productIndex + 1,
            PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
            false
          )
        })
    } catch (e) {
      this.itemIndexObjectStore.setRangeLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
        false
      )
    }
  }

  @action
  updateSingleProductDataFromResponse = (productId, oldProductData, newProductData) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    const updatedProduct = {
      ...oldProductData,
      ...newProductData,
      doorOrderProducts: oldProductData?.doorOrderProducts || {}
    }
    this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
    this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
  }

  @action
  updateSingleProductDoorOrderTotals = (
    productId,
    data: {
      totalSizeQuantity?: number
      totalDoorQuantity?: number
      totalSizeValue?: number
      totalDoorValue?: number
    }
  ) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    const product = toJS(this.itemIndexObjectStore.idObjectMap.get(productId))

    data.totalSizeQuantity !== undefined &&
      (product.doorOrderProductSizeQuantity = data.totalSizeQuantity)
    data.totalDoorQuantity !== undefined &&
      (product.doorOrderProductDoorQuantity = data.totalDoorQuantity)
    data.totalSizeValue !== undefined &&
      (product.doorOrderProductSizeValue = data.totalSizeValue)
    data.totalDoorValue !== undefined &&
      (product.doorOrderProductDoorValue = data.totalDoorValue)

    this.itemIndexObjectStore.idObjectMap.set(productId, product)
    this.itemIndexObjectStore.indexObjectMap.set(productIndex, product)
  }

  @action
  rollUpAllQuantities(rollUpSizeToDoor: boolean, rollUpDoorToProduct: boolean) {
    try {
      const {
        nav: {
          queryParams: {
            orderId,
            buyingSessionGroupId,
            clientType,
            orderType,
            clientOrderId
          }
        },
        productConfiguration: { getViewProductCardAttributes }
      } = stores
      if (!orderId || !buyingSessionGroupId) {
        return undefined
      }
      const buyingSessionProductIds = this.products
      const productIndex = this.itemIndexObjectStore.getIndexByKey(
        buyingSessionProductIds[0]
      )

      this.itemIndexObjectStore.setRangeLoadingForMultipleKeys(
        productIndex,
        productIndex + buyingSessionProductIds.length,
        [
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
        ],
        true
      )

      const payload = {
        buyingSessionProductIds,
        rollUpSizeToDoor,
        rollUpDoorToProduct,
        aggregations: {
          heirarchy: {
            metricAttributes: [
              ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
              ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
            ],
            ...this.getPayloadForUpdatedPageLevelHeirarchy({})
          },
          orderTopbarKPIs: {
            metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
            groupBy: [
              orderType === OrderType.Door
                ? DOOR_ORDER_LEVEL_GROUP_ATTR
                : CLIENT_ORDER_LEVEL_GROUP_ATTR
            ]
          },
          totalDoorHierarchyKPIs: {
            metricAttributes: [
              ...METRIC_ATTRIBUTES_FOR_PRODUCT_QUANTITY,
              ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
            ],
            ...this.getPayloadForUpdatedDoorLevelHeirarchy()
          }
        }
      }
      this.service
        .rollUpAllQuantities(buyingSessionGroupId, orderId, payload)
        .then(resp => {
          const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = resp
          const channelName = getChannelName(clientType)
          const attrConfig = getViewProductCardAttributes(
            channelName,
            this.currentViewName
          )
          const productPayload = {
            includeAllDoorOrders: true,
            filters: {
              clientOrderProductId: buyingSessionProductIds
            },
            excludeFilters: {},
            //excludeFilters: orderType === OrderType.Client ? { doorOrderProductDeleted: [true] } : {},
            attributes: [
              ...uniq(
                MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(
                  attrConfig.map(item => item.attributeId)
                )
              ),
              ...PRODUCT_ATTRIBUTES_FOR_ROLLUP,
              ...this.getListOfWarningAttributes()
            ]
          }
          this.service
            .fetchProductsByGrouping(
              buyingSessionGroupId,
              orderId,
              orderType,
              clientOrderId,
              productPayload
            )
            .then(response => {
              const products = response?.data
              products.forEach(newProduct => {
                const { id } = newProduct
                const productIndex = this.itemIndexObjectStore.getIndexByKey(id)
                this.itemIndexObjectStore.setRangeLoading(
                  productIndex,
                  productIndex + 1,
                  PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
                  true
                )
                const product = this.itemIndexObjectStore.idObjectMap.get(id)
                const updatedProduct = {
                  ...product,
                  ...newProduct
                }
                this.itemIndexObjectStore.idObjectMap.set(id, updatedProduct)
                this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)

                this.itemIndexObjectStore.setRangeLoading(
                  productIndex,
                  productIndex + 1,
                  PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY,
                  false
                )
              })
              // Updating Heirarchy
              this.mutateProductHierarchy(heirarchy)
              this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
              // Updating Topbar
              const topbarData = {
                totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
                totalProductValue: orderTopbarKPIs?.totalProductValue,
                totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
                totalDoorValue: orderTopbarKPIs?.totalDoorValue,
                totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
                totalSizeValue: orderTopbarKPIs?.totalSizeValue
              }
              orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
            })
            .finally(() => {
              this.itemIndexObjectStore.setRangeLoadingForMultipleKeys(
                productIndex,
                productIndex + buyingSessionProductIds.length,
                [
                  PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
                  PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
                ],
                false
              )
            })
        })
      this.setQuantityUpdated(true)
    } catch (e) {}
  }
  @action
  splitAllQuantities(payload, curveTypeToBeUsed) {
    const {
      nav: {
        queryParams: {
          orderId,
          buyingSessionGroupId,
          clientType,
          orderType,
          clientOrderId
        }
      },
      productConfiguration: { getViewProductCardAttributes }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }
    const buyingSessionProductIds = this.products

    const productIndex = this.itemIndexObjectStore.getIndexByKey(
      buyingSessionProductIds[0]
    )

    this.itemIndexObjectStore.setRangeLoadingForMultipleKeys(
      productIndex,
      productIndex + buyingSessionProductIds.length,
      [
        PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
        PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
      ],
      true
    )

    this.service
      .splitAllQuantities(buyingSessionGroupId, orderId, payload)
      .then(resp => {
        const {
          heirarchy,
          totalDoorHierarchyKPIs,
          orderTopbarKPIs,
          productsWithoutDoorCurve,
          productsWithoutSizeCurve
        } = resp
        const channelName = getChannelName(clientType)
        const attrConfig = getViewProductCardAttributes(channelName, this.currentViewName)
        const productPayload = {
          includeAllDoorOrders: true,
          filters: {
            clientOrderProductId: buyingSessionProductIds
          },
          excludeFilters: {},
          attributes: [
            ...uniq(
              MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(
                attrConfig.map(item => item.attributeId)
              )
            ),
            ...PRODUCT_ATTRIBUTES_FOR_ROLLUP,
            ...this.getListOfWarningAttributes()
          ]
        }
        this.service
          .fetchProductsByGrouping(
            buyingSessionGroupId,
            orderId,
            orderType,
            clientOrderId,
            productPayload
          )
          .then(response => {
            const products = response?.data
            products.forEach(newProduct => {
              const { id } = newProduct
              const productIndex = this.itemIndexObjectStore.getIndexByKey(id)

              const product = this.itemIndexObjectStore.idObjectMap.get(id)

              const updatedProduct = {
                ...product,
                ...newProduct
              }
              this.itemIndexObjectStore.idObjectMap.set(id, updatedProduct)
              this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
            })
            // Updating Heirarchy
            this.mutateProductHierarchy(heirarchy)
            this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
            // Updating Topbar
            const topbarData = {
              totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
              totalProductValue: orderTopbarKPIs?.totalProductValue,
              totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
              totalDoorValue: orderTopbarKPIs?.totalDoorValue,
              totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
              totalSizeValue: orderTopbarKPIs?.totalSizeValue,
              totalProductSalesValue: orderTopbarKPIs?.totalProductSalesValue,
              totalDoorSalesValue: orderTopbarKPIs?.totalDoorSalesValue,
              totalSizeSalesValue: orderTopbarKPIs?.totalSizeSalesValue
            }
            orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)

            this.setProductsWithoutCurves({
              productsWithoutDoorCurve,
              productsWithoutSizeCurve,
              curveTypeToBeUsed
            })
          })
      })
      .finally(() => {
        this.itemIndexObjectStore.setRangeLoadingForMultipleKeys(
          productIndex,
          productIndex + buyingSessionProductIds.length,
          [
            PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
            PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
          ],
          false
        )
      })
  }

  @computed get isSplittingInProgress() {
    const buyingSessionProductIds = this.products

    const productIndex = this.itemIndexObjectStore.getIndexByKey(
      buyingSessionProductIds?.[0]
    )
    return this.itemIndexObjectStore.isObjectsDataLoading(
      productIndex,
      productIndex + buyingSessionProductIds.length,
      PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY
    )
  }

  @computed get isProductBookingStatusPending() {
    const buyingSessionProductIds = this.products

    const productIndex = this.itemIndexObjectStore.getIndexByKey(
      buyingSessionProductIds?.[0]
    )
    return this.itemIndexObjectStore.isObjectsDataLoading(
      productIndex,
      productIndex + buyingSessionProductIds.length,
      PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY
    )
  }

  /**
   *
   * @param newHierarchy - received from server
   * @param oldHierarchy - stored in current data
   * @returns
   */

  private enhancedMergeHierarchy = (
    newHierarchy,
    oldHierarchy,
    isProductRemoved = false
  ) => {
    const {
      productConfiguration: { isWarningModeActive }
    } = stores
    const updatedOldHierarchy =
      isProductRemoved || isWarningModeActive
        ? intersectionBy(oldHierarchy, newHierarchy, 'id')
        : oldHierarchy
    const updatedHierarchy = reduce(
      updatedOldHierarchy,
      (acc, hierarchyObj) => {
        const newHierarchyObj = find(newHierarchy, obj => obj?.id === hierarchyObj?.id)
        if (newHierarchyObj) {
          acc.push({
            ...hierarchyObj,
            ...newHierarchyObj,
            totalProducts:
              newHierarchyObj.totalProducts ??
              newHierarchyObj.countOfClientOrderProductId,
            ...(hierarchyObj?.children?.length
              ? {
                  children: this.enhancedMergeHierarchy(
                    newHierarchyObj.children,
                    hierarchyObj.children,
                    isProductRemoved
                  )
                }
              : { children: [] })
          })
        } else acc.push(hierarchyObj)
        return acc
      },
      []
    )
    if (oldHierarchy.length < newHierarchy.length) {
      const newlyAddedHierarchy = differenceBy(newHierarchy, updatedHierarchy, 'id')
      updatedHierarchy.push(...newlyAddedHierarchy)
    }
    return updatedHierarchy
  }

  private mutateHierarchy = (newHierarchy, oldHierarchy, heirarchyToBeRemoved) => {
    const tempHierarchy = cloneDeep(oldHierarchy)
    if (newHierarchy.length) {
      newHierarchy.forEach(newHierarchyObj => {
        let oldObjIndex = tempHierarchy.findIndex(
          item => item?.id === newHierarchyObj?.id
        )
        if (oldObjIndex > -1) {
          const oldObj = tempHierarchy[oldObjIndex]
          if (oldObj) {
            const temp = this.updateHierarchyDataObj(oldObj, newHierarchyObj)
            tempHierarchy[oldObjIndex] = temp
          }
        }
      })
    } else {
      let oldObjIndex = tempHierarchy.findIndex(item => item?.id === heirarchyToBeRemoved)
      oldObjIndex !== -1 && tempHierarchy.splice(oldObjIndex, 1)
    }
    return tempHierarchy
  }

  updateHierarchyDataObj = (oldObj, newObj) => {
    let clonedOldObject = cloneDeep(oldObj || {})
    if (oldObj && newObj) {
      const oldChildren = cloneDeep(oldObj.children || [])
      const newChildren = cloneDeep(newObj.children || [])
      delete oldObj.children
      delete newObj.children
      delete clonedOldObject.children

      clonedOldObject = {
        ...clonedOldObject,
        ...cloneDeep(newObj)
      }
      if (newChildren.length) {
        const tempChild = []
        clonedOldObject.children = newChildren.forEach(newChild => {
          const oldChild = oldChildren.find(oldItem => oldItem.id === newChild.id)
          if (oldChild) {
            tempChild.push(this.updateHierarchyDataObj(oldChild, newChild))
          }
        })
        clonedOldObject.children = tempChild
      } else {
        clonedOldObject.children = []
      }
    }
    clonedOldObject.children = clonedOldObject.children.filter(child => !!child)
    return clonedOldObject
  }

  @action
  destroy() {
    super.destroy()
    this.overviewReactions.forEach(disposer => disposer())
    this.overviewReactions = []
  }

  @computed get activities() {
    const hierarchy = map(this.hierarchyWithoutFilters, 'name')
    return join(hierarchy, CATEGORY_UI_SEPERATOR)
  }

  @computed get products() {
    const productIds = []
    const iterator = this.itemIndexObjectStore.idObjectMap.entries()
    let nextRes = iterator.next()
    while (!nextRes.done) {
      productIds.push(nextRes.value[0])
      nextRes = iterator.next()
    }
    return productIds
  }

  // *********** Quantity Sub Section ***********
  @action
  async updateDoorOrderDraftQuantity(productId: string, payload: any, updatedProduct) {
    const {
      nav: {
        queryParams: {
          orderId: navOrderId,
          orderType,
          clientOrderId: navClientOrderId,
          buyingSessionGroupId
        }
      },
      axios: { setLoadState }
    } = stores
    if (!navOrderId || !buyingSessionGroupId) {
      return undefined
    }
    const clientOrderId = orderType === OrderType.Door ? navClientOrderId : navOrderId
    try {
      setLoadState(true) // show global top bar loader during debounce

      // optimistic updates
      const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
      this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
      this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)

      const { isPrimaryResponse: isPrimaryUpdateResponse } = await this.debouncePool.add(
        {
          key: `updateDoorOrderDraftQuantity.productId=${productId}.doorOrderId=${payload.doorOrderId}`,
          compareFn: () => true // always poool based on key – we only need one of these requests for any product + door
        },
        this.service.updateDoorOrderDraftQuantity,
        buyingSessionGroupId,
        productId,
        clientOrderId,
        payload.doorOrderId,
        payload
      )
      if (!isPrimaryUpdateResponse) {
        return
      }

      // Aggregate query needs to be done separately from the patch since it targets a specific product row
      await this.refreshAggregateDataWithProductRowTotals(
        productId,
        buyingSessionGroupId,
        navOrderId,
        orderType,
        clientOrderId
      )
    } catch (e) {
      // A value in the UI is incorrect - refresh from server (unmanaged - may conflict with other updates)
      this.refreshProductRow(productId, false)
      console.log('error', e)
    }

    setLoadState(false)
  }

  /* updateDoorOrderSizeQuantity – removed during draftQuantity refactor, see git history */

  @action mutateProductHierarchy = (heirarchy, heirarchyToBeUpdated = '') => {
    this.processNewHierarchy(heirarchy)
    this.heirarchy = this.mutateHierarchy(
      heirarchy?.children,
      this.heirarchy,
      heirarchyToBeUpdated
    )
  }

  @action
  updateProductHierarchy = (heirarchy, isProductRemoved = false) => {
    this.processNewHierarchy(heirarchy)
    this.heirarchy = this.enhancedMergeHierarchy(
      heirarchy?.children,
      this.heirarchy,
      isProductRemoved
    )
  }

  @action
  updateTotalDoorHierarchy = (totalDoorHierarchyKPIs, isProductRemoved = false) => {
    this.processNewHierarchy(totalDoorHierarchyKPIs)
    this.totalDoorKPIs = this.enhancedMergeHierarchy(
      totalDoorHierarchyKPIs?.children,
      this.totalDoorKPIs,
      isProductRemoved
    )
  }

  @action removeUnFilteredProduct = (singleProductAggregation, productId) => {
    const {
      productConfiguration: { isWarningModeActive }
    } = stores
    if (!singleProductAggregation?.children?.length) {
      const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
      if (productIndex !== -1) {
        this.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
        if (isWarningModeActive) {
          this.enhancedWarningPopupStore.fetchOrderWarningsForDropdown(false)
        }
      }
    }
  }

  private setBookUnbookLoaders = (
    index: number,
    ids: string[],
    isBook: boolean,
    isAll: boolean,
    active: boolean
  ) => {
    const Key = PRODUCT_OPERATIONS_LOADERS
    const oneKey = isBook ? Key.BOOK_DOOR : Key.UNBOOK_DOOR
    const allKey = isBook ? Key.BOOKING_ALL_DOORS : Key.UNBOOKING_ALL_DOORS
    const pendingKey = Key.BOOK_UNBOOK_IN_PROGRESS
    ids?.forEach(id =>
      this.itemIndexObjectStore.setObjectLoading(index, `${oneKey}_${id}`, active)
    )
    if (isAll) {
      this.itemIndexObjectStore.setObjectLoading(index, allKey, active)
    }
    this.itemIndexObjectStore.setObjectLoading(index, pendingKey, active)
    stores.axios.setLoadState(active) // show global top bar loader during debounce
  }

  // Quantity
  @action
  bookUnBookDoors = async (
    clientOrderId,
    productId,
    doorOrderIds,
    isBook,
    removeIfEmpty,
    doorIds?,
    isAll?
  ) => {
    const {
      nav: {
        queryParams: { orderId, orderType, buyingSessionGroupId, clientType, bspId }
      },
      productConfiguration: { isWarningModeActive }
    } = stores
    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }

    const initialProductState = this.itemIndexObjectStore.idObjectMap.get(productId)
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)

    // Reducer function to merge multiple book/unbook requests (OrderType.Client only since Door is one at a time)
    type ModifyFnArgs = [string, string, any, string, any, string]
    let combinedDoorOrderIds = doorOrderIds
    let modifyFn: (args1: ModifyFnArgs, args2: ModifyFnArgs) => false | ModifyFnArgs
    if (orderType === OrderType.Client) {
      modifyFn = (args1: ModifyFnArgs, args2: ModifyFnArgs) => {
        const [
          productId1,
          orderId1,
          buyingSessionGroupId1,
          clientOrderId1 = '',
          body1,
          orderType1
        ] = args1

        const [productId2, , , , body2] = args2

        if (productId1 !== productId2) {
          return false // Not pooling across product rows yet
        }

        const mergedBody = isAll // Note: this isAll override won't work across book/unbook since those are different requests
          ? body1
          : {
              aggregations: mergeDeep(body1.aggregations, body2.aggregations),
              doorOrderIds: uniq([body1.doorOrderIds, body2.doorOrderIds].flat())
            }

        combinedDoorOrderIds = mergedBody.doorOrderIds

        return [
          productId1,
          orderId1,
          buyingSessionGroupId1,
          clientOrderId1,
          mergedBody,
          orderType1
        ] as ModifyFnArgs
      }
    }

    try {
      // get product maps to update
      let updatedDoorOrderProducts = initialProductState?.doorOrderProducts || {}
      const productAttributes = [
        ...PRODUCT_ATTRIBUTES_FOR_ROLLUP,
        ...this.avgDepthAndTotalStoresAttributes
      ]
      if (isBook) {
        this.setBookUnbookLoaders(productIndex, doorOrderIds, true, isAll, true)
        const body = {
          ...(orderType !== OrderType.Door &&
            clientType !== VIPClientType.Internal && { doorOrderIds }),
          aggregations: {
            ...(isWarningModeActive || this.avgDepthAndTotalStoresAttributes.length
              ? {
                  heirarchy: {
                    metricAttributes: [
                      'totalProducts',
                      ...this.avgDepthAndTotalStoresAttributes
                    ],
                    ...this.getPayloadForUpdatedPageLevelHeirarchy(initialProductState)
                  }
                }
              : {}),
            totalDoorHierarchyKPIs: {
              metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
              ...this.getPayloadForUpdatedDoorLevelHeirarchy()
            },
            ...this.getPayloadForSingleProductAggregation(productId)
          }
        }

        const { response: bookResponse, isPrimaryResponse: isPrimaryBookResponse } =
          await this.debouncePool.add(
            {
              key: 'bookUnBookDoors.add',
              modifyFn,
              resetTimerIf: (prevKey: string) => prevKey === 'bookUnBookDoors.remove'
            },
            this.service.addProductToEnhancedOrder,
            productId,
            orderType !== OrderType.Door ? clientOrderId : doorOrderIds[0],
            buyingSessionGroupId,
            orderType !== OrderType.Door ? '' : clientOrderId,
            body,
            orderType
          )
        if (!isPrimaryBookResponse) {
          return bookResponse
        }

        // Not debounced! We did that already - now finalize quickly.
        const isPrimaryProductResponse = await this.refreshProductRow(
          productId,
          false,
          productAttributes
        )
        if (!isPrimaryProductResponse) {
          return bookResponse
        }

        const { totalDoorHierarchyKPIs, heirarchy } = bookResponse
        //Removing Product from warning mode filters if product has no warnings
        this.removeUnFilteredProduct(bookResponse?.singleProductAggregation, productId)
        if (isWarningModeActive || this.avgDepthAndTotalStoresAttributes.length) {
          this.mutateProductHierarchy(heirarchy, initialProductState?.hierarchyItems?.[0])
        }
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)

        this.setBookUnbookLoaders(productIndex, combinedDoorOrderIds, true, isAll, false) // not using finally block so this only happens after primary response completes
        return bookResponse
      } else {
        // unbook
        this.setBookUnbookLoaders(productIndex, doorOrderIds, false, isAll, true)

        // optimistic reset of product-level totals
        if (isAll) {
          this.updateSingleProductDoorOrderTotals(productId, {
            totalSizeQuantity: 0,
            totalDoorQuantity: 0
          })
        }
        // TODO: else, pre-calc totals for single-door case. Need to formalize where this gets done (e.g. see EnhancedListUIStore updateDoorQuantities for similar calcs)

        let orderAggregation: any =
          orderType === OrderType.Door
            ? { ...DOOR_KPI_ORDER_LEVEL_AGGR }
            : {
                aggregations: {
                  orderTopbarKPIs: {
                    metricAttributes: [
                      ...KPI_TOPBAR_METRIC_ATTRIBUTES,
                      ...this.avgDepthAndTotalStoresAttributes
                    ],
                    groupBy: [CLIENT_ORDER_LEVEL_GROUP_ATTR]
                  }
                },
                doorOrderIds
              }

        orderAggregation.aggregations = {
          productAggregation: {
            metricAttributes: [
              ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
              ...this.avgDepthAndTotalStoresAttributes
            ],
            filters: { clientOrderProductId: [productId] },
            groupBy: [
              {
                attributeId: 'clientOrderProductId',
                orderBy: 'asc'
              }
            ]
          },
          heirarchy: {
            ...orderAggregation.aggregations.orderTopbarKPIs,
            ...this.getPayloadForUpdatedPageLevelHeirarchy(initialProductState)
          },
          totalDoorHierarchyKPIs: {
            metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
            ...this.getPayloadForUpdatedDoorLevelHeirarchy()
          },
          orderTopbarKPIs: {
            metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
            groupBy: [
              orderType === OrderType.Door
                ? DOOR_ORDER_LEVEL_GROUP_ATTR
                : CLIENT_ORDER_LEVEL_GROUP_ATTR
            ]
          },
          ...this.getPayloadForSingleProductAggregation(productId)
        }

        const { response: unbookResponse, isPrimaryResponse: isPrimaryUnbookResponse } =
          await this.debouncePool.add(
            {
              key: 'bookUnBookDoors.remove',
              modifyFn,
              resetTimerIf: (prevKey: string) => prevKey === 'bookUnBookDoors.add'
            },
            this.service.removeProductFromEnhancedOrder,
            productId,
            orderType === OrderType.Door ? doorOrderIds[0] : clientOrderId,
            buyingSessionGroupId,
            orderType === OrderType.Door ? clientOrderId : '',
            orderAggregation,
            orderType
          )
        if (!isPrimaryUnbookResponse) {
          return unbookResponse
        }

        // Not debounced! We did that already - now finalize quickly.
        const isPrimaryProductResponse = await this.refreshProductRow(
          productId,
          false,
          productAttributes
        )
        if (!isPrimaryProductResponse) {
          return unbookResponse
        }

        const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = unbookResponse
        // remove the deleted door product
        if (doorOrderIds.length > 0) {
          updatedDoorOrderProducts = { ...updatedDoorOrderProducts }
          updatedDoorOrderProducts = omit(updatedDoorOrderProducts, doorIds)
        } else {
          updatedDoorOrderProducts = {}
        }

        if (
          (0 === Object.keys(updatedDoorOrderProducts || {})?.length && removeIfEmpty) ||
          orderType === OrderType.Door
        ) {
          // if only one door order product is present delete the row
          if (!bspId) {
            this.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
          } else {
            this.singleDoorResCache[bspId] = unbookResponse
          }
        }

        //Removing Product from warning mode filters if product has no warnings
        this.removeUnFilteredProduct(unbookResponse?.singleProductAggregation, productId)

        // Updating Heirarchy level data
        this.mutateProductHierarchy(heirarchy, initialProductState?.hierarchyItems?.[0])
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs, true)

        //Updating KPI values of Order Topbar
        const topbarData = {
          totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
          totalDoorValue: orderTopbarKPIs?.totalDoorValue,
          totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
          totalSizeValue: orderTopbarKPIs?.totalSizeValue,
          totalProducts: heirarchy.totalProducts ?? heirarchy.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)

        this.setBookUnbookLoaders(productIndex, combinedDoorOrderIds, false, isAll, false)
        return unbookResponse
      }
    } catch (e) {
      console.error('Error in bookUnBookDoors', e)
      this.setBookUnbookLoaders(productIndex, combinedDoorOrderIds, isBook, isAll, false)

      // A value in the UI is incorrect - refresh from server (unmanaged - may conflict with other updates)
      this.refreshProductRow(productId, false)
      this.refreshAggregateDataWithProductRowTotals(
        productId,
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId
      )
    }
  }

  private enhancedCreateDoorProducts = (doorProductsIds = []) => {
    return reduce(
      doorProductsIds,
      (accumulator, doorProductId) => {
        accumulator[doorProductId] = {
          doorOrderProductDoorQuantity: 0,
          doorOrderProductDoorValue: 0,
          doorOrderProductSizeQuantity: 0,
          doorOrderProductSizeValue: 0,
          doorOrderSizeQuantities: []
        }
        return accumulator
      },
      {}
    )
  }

  @action
  bulkUpdateDraftQuantity = (summary, productId, payload) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)

    const clientOrderId = summary?.clientOrder?.clientOrderId || summary?.clientOrder?.id

    const {
      queryParams: { orderId, buyingSessionGroupId, orderType }
    } = nav

    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }

    this.itemIndexObjectStore.setObjectLoading(
      productIndex,
      PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
      true
    )

    return this.service
      .bulkUpdateProductQty(buyingSessionGroupId, clientOrderId, productId, payload)
      .then(async resp => {
        const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = resp
        let doorOrderProducts =
          orderType === OrderType.Client ? product.doorOrderProducts : {}
        payload.bulkDoorOrderQuantities.forEach(
          ({ doorOrderId, draftQuantity, sizeQuantity }) => {
            const doorId = find(
              summary?.doorOrders,
              doorOrder => doorOrder?.id === doorOrderId
            )?.door?.id
            const doorProductSizeQuantity =
              find(payload.bulkDoorOrderQuantities, ['doorOrderId', doorOrderId])
                ?.sizeQuantities || []
            Object.keys(doorProductSizeQuantity).forEach(sizeId => {
              doorProductSizeQuantity[sizeId] = {
                ...doorOrderProducts?.doorOrderSizeQuantities?.[sizeId],
                sizeId,
                quantity: doorProductSizeQuantity?.[sizeId]
              }
            })
            if (orderType === OrderType.Door) {
              doorOrderProducts = {
                doorOrderSizeQuantities: doorProductSizeQuantity || {},
                doorOrderProductDoorQuantity: draftQuantity,
                doorOrderProductSizeQuantity: sizeQuantity
              }
            } else {
              doorOrderProducts[doorId] = {
                ...doorOrderProducts[doorId],
                doorOrderSizeQuantities: doorProductSizeQuantity || {},
                doorOrderProductDoorQuantity: draftQuantity,
                doorOrderProductSizeQuantity: sizeQuantity
              }
            }
          }
        )
        const updatedProduct = {
          ...product,
          ...(orderType === OrderType.Door ? doorOrderProducts : { doorOrderProducts })
        }
        const productResp = await this.fetchSingleProductData(productId, [
          'clientOrderProductQuantity',
          'clientOrderProductValue',
          'doorOrderProductDoorQuantity',
          'doorOrderProductDoorValue',
          'doorOrderProductSizeQuantity',
          'doorOrderProductSizeValue'
        ])
        this.updateSingleProductDataFromResponse(productId, updatedProduct, productResp)
        //Removing Product from warning mode filters if product has no warnings
        this.removeUnFilteredProduct(resp?.singleProductAggregation, productId)

        //Updating Heirarchy
        this.mutateProductHierarchy(heirarchy, product?.hierarchyItems?.[0])
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)

        //Updating KPI values of Order Topbar
        const topbarData = {
          totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
          totalDoorValue: orderTopbarKPIs?.totalDoorValue,
          totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
          totalSizeValue: orderTopbarKPIs?.totalSizeValue
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        this.setQuantityUpdated(true)
      })
      .finally(() => {
        this.itemIndexObjectStore.setObjectLoading(
          productIndex,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
          false
        )
      })
  }

  @action
  pasteTemplate = (summary, productId, payload, doorIdsToUnBook, doorIdsToBook) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    let updatedDoorOrderProducts = product?.doorOrderProducts || {}

    const clientOrderId = summary?.clientOrder?.clientOrderId || summary?.clientOrder?.id

    this.itemIndexObjectStore.setObjectLoading(
      productIndex,
      PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
      true
    )
    const {
      queryParams: { orderId, buyingSessionGroupId, orderType }
    } = nav

    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }

    return this.service
      .pasteTemplate(buyingSessionGroupId, clientOrderId, productId, payload)
      .then(async resp => {
        const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = resp

        if (doorIdsToUnBook.length > 0) {
          updatedDoorOrderProducts = { ...updatedDoorOrderProducts }
          updatedDoorOrderProducts = omit(updatedDoorOrderProducts, doorIdsToUnBook)
        }

        updatedDoorOrderProducts = {
          ...updatedDoorOrderProducts,
          ...this.enhancedCreateDoorProducts(doorIdsToBook || [])
        }

        let doorOrderProducts =
          orderType === OrderType.Client ? updatedDoorOrderProducts : {}
        payload.bulkDoorOrderQuantities.forEach(
          ({ doorOrderId, draftQuantity, sizeQuantity }) => {
            const doorId = find(
              summary?.doorOrders,
              doorOrder => doorOrder?.id === doorOrderId
            )?.door?.id
            const doorProductSizeQuantity =
              find(payload.bulkDoorOrderQuantities, ['doorOrderId', doorOrderId])
                ?.sizeQuantities || []
            Object.keys(doorProductSizeQuantity).forEach(sizeId => {
              doorProductSizeQuantity[sizeId] = {
                ...doorOrderProducts?.doorOrderSizeQuantities?.[sizeId],
                sizeId,
                quantity: doorProductSizeQuantity?.[sizeId]
              }
            })
            if (orderType === OrderType.Door) {
              doorOrderProducts = {
                doorOrderSizeQuantities: doorProductSizeQuantity || {},
                doorOrderProductDoorQuantity: draftQuantity,
                doorOrderProductSizeQuantity: sizeQuantity
              }
            } else {
              doorOrderProducts[doorId] = {
                ...doorOrderProducts[doorId],
                doorOrderSizeQuantities: doorProductSizeQuantity || {},
                doorOrderProductDoorQuantity: draftQuantity,
                doorOrderProductSizeQuantity: sizeQuantity
              }
            }
          }
        )

        const updatedProduct = cloneDeep({
          ...product,
          ...(orderType === OrderType.Door ? doorOrderProducts : { doorOrderProducts })
        })

        Object.keys(updatedProduct.doorOrderProducts || {}).forEach(door => {
          if (
            updatedProduct.excludedInDoors.includes(door) && // excluded door
            !product?.doorOrderProducts[door] // not booked
          ) {
            // delete the door which is unbooked and excluded from local state
            delete updatedProduct.doorOrderProducts[door]
          }
        })
        const productResp = await this.fetchSingleProductData(productId, [
          'clientOrderProductQuantity',
          'clientOrderProductValue',
          'doorOrderProductDoorQuantity',
          'doorOrderProductDoorValue',
          'doorOrderProductSizeQuantity',
          'doorOrderProductSizeValue',
          'doorOrderSizeQuantities',
          'numberOfStores',
          'excludedInDoors'
        ])
        this.updateSingleProductDataFromResponse(productId, updatedProduct, productResp)

        this.removeUnFilteredProduct(resp?.singleProductAggregation, productId)

        //Updating Heirarchy
        this.mutateProductHierarchy(heirarchy, product?.hierarchyItems?.[0])
        this.updateTotalDoorHierarchy(
          totalDoorHierarchyKPIs,
          Boolean(doorIdsToUnBook.length)
        )

        //Updating KPI values of Order Topbar
        const topbarData = {
          totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
          totalDoorValue: orderTopbarKPIs?.totalDoorValue,
          totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
          totalSizeValue: orderTopbarKPIs?.totalSizeValue,
          totalProductSalesValue: orderTopbarKPIs?.totalProductSalesValue,
          totalDoorSalesValue: orderTopbarKPIs?.totalDoorSalesValue,
          totalSizeSalesValue: orderTopbarKPIs?.totalSizeSalesValue,
          totalProducts: heirarchy.totalProducts ?? heirarchy.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        this.setQuantityUpdated(true)
      })
      .finally(() => {
        this.itemIndexObjectStore.setObjectLoading(
          productIndex,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
          false
        )
      })
  }

  @action
  updateComment = (
    buyingSessionProductId: string,
    comments: string,
    commentType: string
  ) => {
    const productIndex = this.itemIndexObjectStore.getIndexByKey(buyingSessionProductId)
    this.itemIndexObjectStore.setObjectLoading(
      productIndex,
      `${PRODUCT_OPERATIONS_LOADERS.UPDATING_DRAFT_QUANTITY}_${commentType}`,
      true
    )
    try {
      const {
        queryParams: { buyingSessionGroupId, clientId }
      } = nav
      this.commentService.updateComment(
        buyingSessionGroupId,
        buyingSessionProductId,
        clientId,
        {
          comments,
          commentType
        }
      )
      const product = this.itemIndexObjectStore.idObjectMap.get(buyingSessionProductId)
      const updatedProduct = {
        ...product,
        ...(commentType === 'CM'
          ? {
              cmComments: comments
            }
          : { rmComments: comments })
      }
      this.itemIndexObjectStore.idObjectMap.set(buyingSessionProductId, updatedProduct)
      this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
    } finally {
      this.itemIndexObjectStore.setObjectLoading(
        productIndex,
        `${PRODUCT_OPERATIONS_LOADERS.UPDATING_DRAFT_QUANTITY}_${commentType}`,
        false
      )
    }
  }

  @action
  async removeItemObjectFromHierarchy(
    index: number,
    resFromCache?: Record<string, any>,
    isProductRemoved?: Boolean
  ) {
    try {
      const productHierachy =
        this.itemIndexObjectStore.indexObjectMap.get(index)?.hierarchyItems
      const res = await super.removeItemObjectFromHierarchy(
        index,
        resFromCache,
        isProductRemoved
      )
      //Updating heirarchy level data
      isProductRemoved && this.updateTotalDoorHierarchy(res?.totalDoorHierarchyKPIs, true)
      this.processNewHierarchy(res?.heirarchy)
      isProductRemoved &&
        this.mutateProductHierarchy(res?.heirarchy, productHierachy?.[0])
      this.setQuantityUpdated(true)
    } catch (e) {
      throw e
    } finally {
      this.itemIndexObjectStore.setRangeLoading(
        index,
        index + 1,
        PRODUCT_REMOVE_LOADER_KEY,
        false
      )
    }
  }

  @action
  removeHierarchy(heirarchyStr: string[]) {
    this.heirarchy = [...this.heirarchy]
  }

  @action
  fetchAndPrepareFilterAttributesData = viewName => {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          clientType,
          clientOrderId,
          orderId,
          orderType
        }
      },
      productConfiguration: { getActionAttributes }
    } = stores

    if (!orderId || !buyingSessionGroupId) {
      return undefined
    }

    const filterAttributes = getActionAttributes(
      'filters',
      getChannelName(clientType),
      viewName
    )
    const aggregateAttributes = map(filterAttributes, 'id')
    const requestBody = {
      aggregateAttributes: aggregateAttributes
    }
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrder/${orderId}/aggregate-attributes`
        : `/clientOrder/${clientOrderId}/doorOrder/${orderId}/aggregate-attributes`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}${orderPath}`

    if (aggregateAttributes.length) {
      this.cnfProductFilterStore.fetchFilterAttributeValues(
        url,
        requestBody,
        filterAttributes,
        aggregateAttributes
      )
    }
  }

  @action
  getSearchTextParam = () => {
    return isEmptyValue(this.searchKey)
      ? {}
      : {
          search: this.searchKey
        }
  }

  get getProductIdAttributeForOrderProduct() {
    const {
      queryParams: { orderType }
    } = nav

    return orderType === OrderType.Door ? 'doorOrderProductId' : 'clientOrderProductId'
  }

  getPayloadForSingleProductAggregation = (
    productId: string,
    metricAttributes?: String[]
  ) => {
    const productIdAttribute = this.getProductIdAttributeForOrderProduct
    return {
      singleProductAggregation: {
        metricAttributes: metricAttributes ? metricAttributes : ['totalProducts'],
        filters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
          ...this.getAppliedWarningFilterData(),
          [productIdAttribute]: [productId]
        },
        excludeFilters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
        },
        groupBy: [
          {
            attributeId: productIdAttribute,
            orderBy: 'asc'
          }
        ]
      }
    }
  }

  getPayloadForPageAndSingleProductAggregation = (
    product: any,
    orderType: OrderType | string
  ) => {
    return {
      heirarchy: {
        metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
        ...this.getPayloadForUpdatedPageLevelHeirarchy(product)
      },
      totalDoorHierarchyKPIs: {
        metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
        ...this.getPayloadForUpdatedDoorLevelHeirarchy()
      },
      orderTopbarKPIs: {
        metricAttributes: METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
        groupBy: [
          orderType === OrderType.Door
            ? DOOR_ORDER_LEVEL_GROUP_ATTR
            : CLIENT_ORDER_LEVEL_GROUP_ATTR
        ]
      },
      ...this.getPayloadForSingleProductAggregation(
        product.id,
        METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
      )
    }
  }

  getPayloadForUpdatedPageLevelHeirarchy = product => {
    return {
      ...this.getAllTopBarSelections(false),
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...(product &&
          product?.hierarchyItems?.length && {
            [product?.hierarchyItemsAtributeIds[0]]: [product?.hierarchyItems[0]]
          }),
        ...this.getAppliedWarningFilterData()
      }
    }
  }

  getPayloadForUpdatedDoorLevelHeirarchy = () => {
    const addedGroups = this.cnfGroupByUIStore.addedGroupByAttributes
    return {
      ...this.getAllTopBarSelections(true),
      ...(addedGroups?.length
        ? {
            groupBy: [
              ...[
                {
                  attributeId: 'doorId',
                  orderBy: 'asc'
                }
              ],
              ...addedGroups
                .filter(({ id }) => id)
                .map(({ id, sortId }) => ({ attributeId: id, orderBy: sortId }))
            ]
          }
        : {
            preAdditionalDefaultGroupBy: [
              {
                attributeId: 'doorId',
                orderBy: 'asc'
              }
            ]
          })
    }
  }

  @action setProductsWithoutCurves = (value: ProductsWithoutCurves) => {
    this.productsWithoutCurves = value
  }
  @action
  bookUnbookAllProductsForADoorFromEnhancedClientOrder = (
    doorOrderId,
    isUnbookAllOperation
  ) => {
    const {
      nav: {
        queryParams: {
          orderId,
          buyingSessionGroupId,
          clientType,
          buyingSession,
          clientId
        }
      }
    } = stores

    const addedGroups = this.cnfGroupByUIStore.addedGroupByAttributes
    const groupByDoors = addedGroups?.length
      ? {
          groupBy: [
            ...[
              {
                attributeId: 'doorId',
                orderBy: 'asc'
              }
            ],
            ...addedGroups
              .filter(({ id }) => id)
              .map(({ id, sortId }) => ({ attributeId: id, orderBy: sortId }))
          ]
        }
      : {
          preAdditionalDefaultGroupBy: [
            {
              attributeId: 'doorId',
              orderBy: 'asc'
            }
          ]
        }

    const isRetail = getChannelName(clientType) === ChannelSubSection.Retail

    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...(isRetail ? { 'buyingSession-id': [buyingSession] } : {}),
        'original-active': [true],
        channels: [getTitleCaseChannelName(clientType)],
        allClustersExcluded: [false]
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {}),
        'excludedClients-id': [clientId]
      },
      doorOrderIds: [doorOrderId],
      ...this.getSearchTextParam(),
      aggregations: {
        heirarchy: {
          ...this.getAllTopBarSelections(true),
          metricAttributes: [
            'totalProductQuantity',
            'countOfClientOrderProductId',
            ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY,
            ...this.avgDepthAndTotalStoresAttributes
          ]
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: [
            ...['totalProductQuantity'],
            ...METRIC_ATTRIBUTES_FOR_DOOR_AND_SIZE_QUANTITY
          ],
          ...this.getAllTopBarSelections(true),
          ...groupByDoors
        },
        orderTopbarKPIs: {
          metricAttributes: [...KPI_TOPBAR_METRIC_ATTRIBUTES],
          groupBy: [CLIENT_ORDER_LEVEL_GROUP_ATTR]
        }
      }
    }
    return isUnbookAllOperation
      ? this.service.removeAllProductsFromEnhancedClientOrder(
          orderId,
          buyingSessionGroupId,
          body
        )
      : this.service.addAllProductsToEnhancedClientOrder(
          orderId,
          buyingSessionGroupId,
          body
        )
  }

  @action
  updateAllProductBookingsForDoor = async (doorOrderId, isUnbookAllOperation) => {
    const {
      nav: {
        queryParams: { clientType }
      }
    } = stores

    try {
      const {
        nav: {
          queryParams: { orderId, buyingSessionGroupId, orderType, clientOrderId }
        },
        productConfiguration: { defaultValuesFromConfig, getViewProductCardAttributes }
      } = stores

      if (!orderId || !buyingSessionGroupId) {
        return undefined
      }
      const buyingSessionProductIds = this.products

      const productIndex = this.itemIndexObjectStore.getIndexByKey(
        buyingSessionProductIds[0]
      )

      this.itemIndexObjectStore.setRangeLoadingForMultipleKeys(
        productIndex,
        productIndex + buyingSessionProductIds.length,
        [
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
        ],
        true
      )
      this.bookUnbookAllProductsForADoorFromEnhancedClientOrder(
        doorOrderId,
        isUnbookAllOperation
      )
        .then(async response => {
          const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = response
          this.processNewHierarchy(totalDoorHierarchyKPIs)
          this.totalDoorKPIs = totalDoorHierarchyKPIs?.children ?? []
          const tempData = cloneDeep(heirarchy)
          this.processNewHierarchy(tempData)
          formatProductGroupsData(tempData, defaultValuesFromConfig?.empty)
          // Updating Topbar
          const topbarData = {
            ...orderTopbarKPIs,
            totalProducts:
              orderTopbarKPIs.totalProducts ?? orderTopbarKPIs.countOfClientOrderProductId
          }
          orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)

          const channelName = getChannelName(clientType)
          const attrConfig = getViewProductCardAttributes(
            channelName,
            this.currentViewName
          )

          const productPayload = {
            includeAllDoorOrders: true,
            filters: {
              ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
              ...this.getAppliedWarningFilterData()
            },
            attributes: [
              ...uniq(
                MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(
                  attrConfig.map(item => item.attributeId)
                )
              ),
              ...this.getListOfWarningAttributes(),
              ...(clientType === getEnumValue(VIPClientType.Internal)
                ? MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_RETAIL_QUANTITY
                : MANDATORY_ATTRIBUTE_FOR_LIST_VIEW_IN_WHOLESALE)
            ]
          }
          const productsResponse = await this.service.fetchProductsByGrouping(
            buyingSessionGroupId,
            orderId,
            orderType,
            clientOrderId,
            productPayload
          )

          const products = productsResponse?.data
          products.forEach(newProduct => {
            const { id } = newProduct
            const productIndex = this.itemIndexObjectStore.getIndexByKey(id)

            const product = this.itemIndexObjectStore.idObjectMap.get(id)

            const updatedProduct = {
              ...product,
              ...newProduct
            }
            this.itemIndexObjectStore.idObjectMap.set(id, updatedProduct)
            this.itemIndexObjectStore.indexObjectMap.set(productIndex, updatedProduct)
          })
          // Updating Heirarchy
          this.mutateProductHierarchy(heirarchy)
          this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
          orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        })
        .finally(() => {
          this.itemIndexObjectStore.setRangeLoadingForMultipleKeys(
            productIndex,
            productIndex + buyingSessionProductIds.length,
            [
              PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY,
              PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
            ],
            false
          )
        })
    } catch (error) {
      console.error(error)
      return undefined
    }
  }
}
